// The state machine of one rendered form: values, errors, the resolved visibility, repeat instances and the submit
// flow. It has no React in it - useFormRenderer wraps it with useSyncExternalStore - so every rule of spec §4, §6 and
// §8 is tested by calling methods and reading snapshots. No aliases: runs under `node --test`.
import { get_field_state, resolve_form_state, validate_submission, validate_extra_meta, clean_page_url } from "./form_helper.js";
import { DEFAULT_MAX_FILE_BYTES } from "./constants.js";
import {
    custom_rule_errors, stored_files_of, initial_values, write_field, add_instance, remove_instance, without_errors, without_group_errors, errors_to_map,
    first_error_path, interpret_submit_failure, build_submit_input, groups_of, error_path, read_field,
} from "./form_logic.js";
import { input_files_of, data_url_to_blob, with_uploaded_files } from "./form_logic.js";

/*
 * create_form_controller({
 *   definition,        what getFormDefinition returned (id, key, lang, requires_login, captcha_enabled, version: { version, groups })
 *   extra_meta,        { key: string | number | boolean } the site adds to meta.extra
 *   max_file_bytes,    the largest file sent (default 3.5 MB, see constants.js)
 *   geo,               { states, cities } to check state/city membership while validating (optional)
 *   is_logged_in,      () => boolean
 *   on_login_required, on_success({ id, duplicate, values }), on_error({ kind, errors, status })
 *   get_custom_rules,  () => { [custom_key]: (value, field, values) => code | null } the site's rules for its own fields
 *   get_submission_id, () => id of the visitor's own answer (a draft, or one sent before) this send changes; none: a new answer
 *   get_captcha,       async () => token | undefined   (called only when the form has the captcha on)
 *   submit,            async (input) => { status, data, errors }   the createFormSubmission service
 *   now,               () => ms             page_url, () => string      (injected for tests)
 *   warn,              console.warn
 * })
 */
export const create_form_controller = (options = {}) => {
    const {
        definition, extra_meta, max_file_bytes = DEFAULT_MAX_FILE_BYTES, is_logged_in = () => true, on_login_required, on_success, on_error,
        get_captcha, get_custom_rules, get_submission_id, submit, now = () => Date.now(), page_url = () => (typeof window !== "undefined" ? window.location.href : ""),
        warn = (...args) => console.warn(...args),
    } = options;
    // async ({ captcha, files: [{ path, blob, file_name }] }) => { status, data: { files: [{ path, upload_id, upload_token }] }, errors }:
    // the chosen files are sent before the answer (see step 4b of submit); none given, they go inside the answer as before
    const { upload_files } = options;
    const version = definition?.version;

    const listeners = new Set();
    let started_at_ms = null;
    let state = null;
    let extra = extra_meta;
    let geo = options.geo;

    const resolve = (values) => resolve_form_state(version, values);

    const make_state = (patch) => {
        const values = patch.values ?? state?.values ?? initial_values(version);
        // the visibility is recomputed only when the values changed
        const form_state = patch.values || !state ? resolve(values) : state.form_state;
        return {
            values,
            form_state,
            errors: {},
            submitting: false,
            submitted: false,
            duplicate: false,
            submission_id: undefined,
            focus_path: null,
            ...(state || {}),
            ...patch,
            form_state,
        };
    };

    const set_state = (patch) => {
        state = make_state(patch);
        listeners.forEach((listener) => listener());
    };

    state = make_state({ values: initial_values(version) });

    const start_clock = () => {
        if (started_at_ms === null) {
            started_at_ms = now();
        }
    };

    const change_field = (group, index, field_key, value) => {
        start_clock();
        let values = write_field(state.values, group, index, field_key, value);
        let errors = without_errors(state.errors, error_path(group, index, field_key));
        // a city belongs to a state: picking another state empties the cities of the same group (and instance)
        const changed = (group.fields || []).find((field) => field.key === field_key);
        if (changed?.type === "state" && read_field(state.values, group, index, field_key)?.value !== value?.value) {
            (group.fields || []).filter((field) => field.type === "city").forEach((city) => {
                values = write_field(values, group, index, city.key, undefined);
                errors = without_errors(errors, error_path(group, index, city.key));
            });
        }
        set_state({ values, errors, focus_path: null });
    };

    const find_group = (group_key) => groups_of(version).find((group) => group?.key === group_key);

    // the same checks the server makes, and the site's rules for its own fields on top of them
    const check_all = () => {
        // a file that came back from the server (an edit) is not read again: it is put back after the check
        const stored = stored_files_of(version, state.values);
        const first = validate_submission(version, stored.values, { mode: "input", geo, limits: { FILE_MAX_BYTES: max_file_bytes } });
        const visible = (key) => get_field_state(state.form_state, key, 0)?.visible !== false;
        const checked = {
            ...first,
            errors: first.errors.filter((error) => !(stored.kept[error.key] !== undefined && error.code === "required")),
            clean_values: { ...first.clean_values, ...Object.fromEntries(Object.entries(stored.kept).filter(([key]) => visible(key))) },
        };
        const rules = typeof get_custom_rules === "function" ? get_custom_rules() : undefined;
        const from_site = custom_rule_errors(version, state.values, state.form_state, rules, checked.errors);
        return from_site.length > 0 ? { ...checked, errors: [...checked.errors, ...from_site] } : checked;
    };

    // `notify` is false for the visitor's own mistakes caught before sending: on_error is for what the server or the network did
    const fail = (kind, errors, status, notify = true) => {
        set_state({ submitting: false, errors, focus_path: first_error_path(version, errors), focus_count: (state?.focus_count || 0) + 1 });
        if (notify && typeof on_error === "function") {
            on_error({ kind, errors, status });
        }
        return { status: kind, errors };
    };

    const controller = {
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        get_state: () => state,

        // a field of a plain group: set_value(null, "f_x", v); of a repeatable one: set_value("g_x", "f_y", v, index)
        set_value(group_key, field_key, value, index = 0) {
            const group = group_key === null ? groups_of(version).find((item) => !item?.is_repeatable && (item.fields || []).some((field) => field.key === field_key)) : find_group(group_key);
            if (!group) {
                return;
            }
            change_field(group, group.is_repeatable ? index : 0, field_key, value);
        },
        // A saved draft, or any values the site holds: they replace the values (the visibility is worked out again). Nothing else changes.
        load_values(values) {
            if (values && typeof values === "object") {
                set_state({ values, errors: {}, focus_path: null });
            }
        },

        // For a form shown in steps (a step is some of the groups, by their keys): the checks of the whole submission, kept for the
        // fields of these groups only - so a step is judged on what it shows and the later steps' empty fields say nothing yet.
        // Fills the errors of those fields and takes the focus to the first; the visitor's own mistakes, so on_error is not called.
        // -> { status: "ok" } | { status: "invalid", errors }
        validate_groups(group_keys) {
            const wanted = new Set(group_keys || []);
            const plain_fields = new Set();
            groups_of(version).forEach((group) => {
                if (wanted.has(group?.key) && !group.is_repeatable) {
                    (group.fields || []).forEach((field) => plain_fields.add(field.key));
                }
            });
            const checked = check_all();
            const own = checked.errors.filter((error) => (error.group_key ? wanted.has(error.group_key) : plain_fields.has(error.key)));
            if (own.length === 0) {
                return { status: "ok" };
            }
            const errors = errors_to_map(own);
            return fail("invalid", errors, undefined, false);
        },

        set_field_error(path, codes) {
            set_state({ errors: { ...state.errors, [path]: codes } });
        },
        clear_field_error(path) {
            set_state({ errors: without_errors(state.errors, path) });
        },

        add_instance(group_key) {
            const group = find_group(group_key);
            if (!group) {
                return;
            }
            start_clock();
            set_state({ values: add_instance(state.values, group) });
        },
        remove_instance(group_key, index) {
            const group = find_group(group_key);
            if (!group) {
                return;
            }
            start_clock();
            set_state({ values: remove_instance(state.values, group, index), errors: without_group_errors(state.errors, group_key) });
        },

        set_extra_meta(next) {
            extra = next;
        },
        // the states and cities, once loaded: used to check a state/city value the way the server does
        set_geo(next) {
            geo = next;
        },

        // back to an empty form (after a success, to send another answer)
        reset() {
            started_at_ms = null;
            state = null;
            set_state({ values: initial_values(version) });
        },

        async submit() {
            if (state.submitting) {
                return { status: "busy" };
            }
            // 1. login (spec §8): the form may be filled, but not sent
            if (definition?.requires_login && !is_logged_in()) {
                set_state({ errors: { _form: ["login_required"] } });
                if (typeof on_login_required === "function") {
                    on_login_required();
                }
                return { status: "login_required" };
            }
            // 2. the same checks the server makes, so the visitor sees them at once
            const checked = check_all();
            if (checked.errors.length > 0) {
                const errors = errors_to_map(checked.errors);
                return fail("invalid", errors, undefined, false);
            }
            set_state({ submitting: true, errors: {}, focus_path: null });

            // the files to send before the answer (only with `upload_files`): the answer's token is then taken after their upload
            const files = typeof upload_files === "function" ? input_files_of(version, checked.clean_values) : [];

            // 3. the captcha token, when the form asks for one
            let captcha;
            if (files.length === 0 && definition?.captcha_enabled !== false && typeof get_captcha === "function") {
                try {
                    captcha = await get_captcha();
                } catch {
                    captcha = undefined;
                }
            }

            // 4. extra_meta comes from the site: a bad one is left out (with a warning) rather than failing the visitor's form
            let meta_extra;
            if (extra !== undefined && extra !== null) {
                const result = validate_extra_meta(extra);
                if (result.errors.length > 0) {
                    warn("[form-renderer] extra_meta was not sent:", result.errors.map((item) => (item.key ? `${item.code} (${item.key})` : item.code)).join(", "));
                } else {
                    meta_extra = result.extra;
                }
            }

            // 4b. the chosen files go to the server first, as files (multipart, with their own solved captcha), when the site asked
            // for it (`upload_files`, jument); the answer then names each one by its upload. Without the option nothing changes.
            let sent_values = checked.clean_values;
            if (files.length > 0) {
                let upload_captcha;
                if (typeof get_captcha === "function") {
                    try {
                        upload_captcha = await get_captcha();
                    } catch {
                        upload_captcha = undefined;
                    }
                }
                let uploaded;
                try {
                    uploaded = await upload_files({
                        captcha: upload_captcha,
                        files: files.map(({ path, value }) => ({ path, blob: data_url_to_blob(value.content), file_name: value.file_name })),
                    });
                } catch {
                    uploaded = { status: false, errors: undefined };
                }
                const placed = uploaded?.status ? with_uploaded_files(checked.clean_values, files.map((item) => item.path), uploaded.data?.files) : null;
                if (!placed || placed.missing.length > 0) {
                    const failure = placed ? { kind: "unknown", errors: { _form: ["file_upload_failed"] } } : interpret_submit_failure(uploaded?.errors);
                    if (failure.kind === "login_required" && typeof on_login_required === "function") {
                        on_login_required();
                    }
                    return fail(failure.kind, failure.errors, uploaded?.errors?.[0]?.code);
                }
                sent_values = placed.values;
                // a token is spent once: the answer gets one of its own, after the upload
                if (definition?.captcha_enabled !== false && typeof get_captcha === "function") {
                    try {
                        captcha = await get_captcha();
                    } catch {
                        captcha = undefined;
                    }
                }
            }

            const input = build_submit_input({
                definition,
                clean_values: sent_values,
                submission_id: typeof get_submission_id === "function" ? get_submission_id() : undefined,
                started_at_ms,
                now_ms: now(),
                page_url: clean_page_url(page_url()),
                extra: meta_extra,
                captcha,
            });

            // 5. send
            let result;
            try {
                result = await submit(input);
            } catch {
                result = { status: false, errors: undefined };
            }

            if (result?.status) {
                const duplicate = result.data?.duplicate === true;
                set_state({ submitting: false, submitted: true, duplicate, submission_id: result.data?.id, errors: {} });
                if (typeof on_success === "function") {
                    on_success({ id: result.data?.id, duplicate, values: checked.clean_values });
                }
                return { status: "success", duplicate, id: result.data?.id };
            }

            // 6. the server said no: show it on the fields it names
            const failure = interpret_submit_failure(result?.errors);
            if (failure.kind === "login_required" && typeof on_login_required === "function") {
                on_login_required();
            }
            return fail(failure.kind, failure.errors, result?.errors?.[0]?.code);
        },
    };

    return controller;
};
